/**
 * React hook for logging (logging-strategy POLICY.md §1's first bullet).
 *
 * Reads `sessionId` and `leadId` from LoggerContext (must be mounted under
 * `LoggerProvider`) and layers in an optional per-call `userId`. Identity
 * stays at the call site that owns it instead of making the provider's context
 * mutable when auth resolves.
 */
import type { Logger } from "@bindery/logging";
import { useMemo } from "react";
import { createLogger, makeContextProvider } from "./backend";
import { useLoggerContext } from "./LoggerContext";

export interface UseLoggerOptions {
  /**
   * Your auth provider's opaque subject, to include on every event from this
   * Logger when it is known — never an email, display name, or database id
   * (logging-strategy POLICY.md §3).
   */
  userId?: string;
}

export function useLogger(options?: UseLoggerOptions): Logger {
  const { sessionId, leadId } = useLoggerContext();
  const userId = options?.userId;

  return useMemo(
    () => createLogger(makeContextProvider(sessionId, userId, leadId)),
    [sessionId, userId, leadId],
  );
}
